/**
 * VendorFormDialog — create/edit vendor.
 * RHF + yup validation.  `code` chỉ nhập khi mode === 'create'.
 */
import { yupResolver } from '@hookform/resolvers/yup'
import { Autocomplete, Box, TextField } from '@mui/material'
import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'

import FormDialog from '@/components/FormDialog'
import {
  useCreateVendorMutation,
  useUpdateVendorMutation,
} from '@/store/api/vendorsApi'
import { useSnackbar } from '@/hooks/useSnackbar'
import { getErrorMessage } from '@/utils/error'
import type { Vendor, VendorType } from '@/types/api'

interface FormValues {
  code: string
  name: string
  short_name: string
  address: string
  phone: string
  mobile: string
  email: string
  vat_code: string
  type: '' | VendorType
  note: string
}

const EMPTY: FormValues = {
  code: '',
  name: '',
  short_name: '',
  address: '',
  phone: '',
  mobile: '',
  email: '',
  vat_code: '',
  type: '',
  note: '',
}

const schema: yup.ObjectSchema<FormValues> = yup.object({
  code: yup
    .string()
    .trim()
    .required('Code is required')
    .max(20, 'Max 20 characters')
    .matches(/^[A-Z0-9_\-]+$/i, 'Only letters, digits, dash, underscore'),
  name: yup.string().trim().required('Name is required').max(200),
  short_name: yup.string().trim().max(100).default(''),
  address: yup.string().trim().max(255).default(''),
  phone: yup.string().trim().max(50).default(''),
  mobile: yup.string().trim().max(50).default(''),
  email: yup.string().trim().email('Invalid email').max(120).default(''),
  vat_code: yup.string().trim().max(20).default(''),
  type: yup
    .mixed<'' | VendorType>()
    .oneOf(['', 0, 1, 2] as const)
    .default(''),
  note: yup.string().trim().max(500).default(''),
})

export interface VendorFormDialogProps {
  open: boolean
  mode: 'create' | 'edit'
  initialValue?: Vendor | null
  onClose: () => void
}

export function VendorFormDialog({ open, mode, initialValue, onClose }: VendorFormDialogProps) {
  const snack = useSnackbar()
  const [createVendor, { isLoading: creating }] = useCreateVendorMutation()
  const [updateVendor, { isLoading: updating }] = useUpdateVendorMutation()

  const isEdit = mode === 'edit'
  const loading = creating || updating

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: EMPTY,
  })

  useEffect(() => {
    if (!open) return
    if (isEdit && initialValue) {
      reset({
        code: initialValue.code,
        name: initialValue.name ?? '',
        short_name: initialValue.short_name ?? '',
        address: initialValue.address ?? '',
        phone: initialValue.phone ?? '',
        mobile: initialValue.mobile ?? '',
        email: initialValue.email ?? '',
        vat_code: initialValue.vat_code ?? '',
        type: initialValue.type ?? '',
        note: initialValue.note ?? '',
      })
    } else {
      reset(EMPTY)
    }
  }, [open, isEdit, initialValue, reset])

  const onSubmit = handleSubmit(async (values) => {
    const payload: Vendor = {
      code: values.code.trim(),
      name: values.name.trim(),
      short_name: values.short_name || null,
      address: values.address || null,
      phone: values.phone || null,
      mobile: values.mobile || null,
      email: values.email || null,
      vat_code: values.vat_code || null,
      type: values.type === '' ? null : (values.type as VendorType),
      note: values.note || null,
    }
    try {
      if (isEdit && initialValue) {
        await updateVendor({ code: initialValue.code, body: payload }).unwrap()
        snack.success(`Updated vendor ${initialValue.code}`)
      } else {
        await createVendor(payload).unwrap()
        snack.success(`Created vendor ${payload.code}`)
      }
      onClose()
    } catch (err) {
      snack.error(getErrorMessage(err, isEdit ? 'Update failed' : 'Failed to create vendor'))
    }
  })

  return (
    <FormDialog
      open={open}
      title={isEdit ? 'Edit Vendor' : 'New Vendor'}
      loading={loading}
      onClose={onClose}
      onSubmit={() => void onSubmit()}
      maxWidth="md"
    >
      <Box display="flex" flexDirection="column" gap={2} pt={1}>
        <Box display="flex" gap={2} flexWrap="wrap">
          <TextField
            label="Code *"
            size="small"
            sx={{
              flex: '1 1 200px',
              ...(isEdit && {
                // Skill `/convert-form` readonly bg standard
                '& .MuiInputBase-root': {
                  bgcolor: (theme: { palette: { mode: string } }) =>
                    theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f3f5',
                },
                '& .MuiInputBase-input': { color: 'text.secondary', fontWeight: 600 },
              }),
            }}
            {...register('code')}
            error={!!errors.code}
            helperText={errors.code?.message}
            disabled={isEdit}
          />
          <TextField
            label="Name *"
            size="small"
            sx={{ flex: '2 1 320px' }}
            {...register('name')}
            error={!!errors.name}
            helperText={errors.name?.message}
          />
        </Box>
        <Box display="flex" gap={2} flexWrap="wrap">
          <TextField
            label="Short Name"
            size="small"
            sx={{ flex: '1 1 240px' }}
            {...register('short_name')}
            error={!!errors.short_name}
            helperText={errors.short_name?.message}
          />
          <Controller
            control={control}
            name="type"
            render={({ field }) => {
              const opts: { value: 0 | 1 | 2; label: string }[] = [
                { value: 0, label: 'Customer' },
                { value: 1, label: 'Vendor' },
                { value: 2, label: 'Both' },
              ]
              const sel = opts.find((o) => o.value === field.value) ?? null
              return (
                <Autocomplete
                  size="small"
                  sx={{ flex: '1 1 200px' }}
                  options={opts}
                  value={sel}
                  onChange={(_e, v) => field.onChange(v?.value ?? '')}
                  getOptionLabel={(o) => o ? String(o.label ?? '') : ''}
                  isOptionEqualToValue={(a, b) => a.value === b.value}
                  renderInput={(params) => <TextField {...params} label="Type" />}
                />
              )
            }}
          />
        </Box>
        <TextField
          label="Address"
          size="small"
          fullWidth
          {...register('address')}
          error={!!errors.address}
          helperText={errors.address?.message}
        />
        <Box display="flex" gap={2} flexWrap="wrap">
          <TextField
            label="Phone"
            size="small"
            sx={{ flex: '1 1 200px' }}
            {...register('phone')}
            error={!!errors.phone}
            helperText={errors.phone?.message}
          />
          <TextField
            label="Mobile"
            size="small"
            sx={{ flex: '1 1 200px' }}
            {...register('mobile')}
            error={!!errors.mobile}
            helperText={errors.mobile?.message}
          />
          <TextField
            label="Email"
            size="small"
            sx={{ flex: '1 1 240px' }}
            {...register('email')}
            error={!!errors.email}
            helperText={errors.email?.message}
          />
          <TextField
            label="VAT Code"
            size="small"
            sx={{ flex: '1 1 200px' }}
            {...register('vat_code')}
            error={!!errors.vat_code}
            helperText={errors.vat_code?.message}
          />
        </Box>
        <TextField
          label="Note"
          size="small"
          fullWidth
          multiline
          minRows={2}
          {...register('note')}
          error={!!errors.note}
          helperText={errors.note?.message}
        />
      </Box>
    </FormDialog>
  )
}

export default VendorFormDialog
